<template>
  <div>
    <b-row class="mb-2">
      <div class="pl-3 mr-auto">
        <div v-if="!archivedList" class="campaigns-controls">
          <button
            class="btn-link-alt btn-link-primary text-primary mr-3"
            v-b-tooltip.hover
            title="Add new group"
            v-b-modal.new-group-form
          >
            <svg-icon icon="add-group" class="icon-lg" />
          </button>
          <button
            class="btn-link-alt btn-link-primary"
            v-b-tooltip.hover
            title="Stop campaigns"
            :disabled="!selectedCampaigns.length"
            @click="showConfirm('pause')"
          >
            <svg-icon icon="pause" class="icon-xl" />
          </button>
          <button
            class="btn-link-alt btn-link-primary"
            v-b-tooltip.hover
            title="Start campaigns"
            :disabled="!selectedCampaigns.length"
            @click="showConfirm('start')"
          >
            <svg-icon icon="play" class="icon-xl" />
          </button>
          <button
            class="btn-link-alt btn-link-primary"
            v-b-tooltip.hover
            title="Delete campaigns"
            :disabled="!selectedCampaigns.length"
            @click="showConfirm('archive')"
          >
            <svg-icon icon="trash" class="icon-lg" />
          </button>
          <router-link
            class="btn-link-alt btn-link-primary text-info ml-3"
            title="List of 'deleted' campaigns. You can restore archived campaign."
            v-b-tooltip.hover
            to="archived-campaigns"
          >
            Archived campaigns
          </router-link>
        </div>
        <div v-else class="campaigns-controls">
          <button
            class="btn-link-alt btn-link-primary"
            v-b-tooltip.hover
            title="Restore selected campaigns"
            :disabled="!selectedCampaigns.length"
            @click="showConfirm('restore')"
          >
            <svg-icon icon="loop" class="icon-xl" />
          </button>
        </div>
      </div>
      <div class="text-right pr-3 campaigns-controls table-controls">
        <b-dropdown
          class="float-right table-columns-toggler"
          toggle-class="p-0 border-0 mr-0"
          right
          variant="link"
          v-b-tooltip.hover
          title="Toggle columns"
          size="lg"
          no-caret
        >
          <template slot="button-content">
            <!-- Toggle columns -->
            <svg-icon icon="table" class="icon-xl" />
          </template>

          <div v-for="(field, index) in tableFields" :key="index">
            <b-form-checkbox
              v-model="fields[field].class"
              value=""
              unchecked-value="d-none"
            >
              {{ fields[field].label !== '' ? fields[field].label : 'Actions' }}
            </b-form-checkbox>
          </div>
        </b-dropdown>
        <button
          class="btn-link-alt btn-link-primary"
          v-b-tooltip.hover
          title="Download CSV"
          @click="downloadCSV({ filename: 'campaigns.csv' })"
        >
          <svg-icon icon="download" class="icon-xl" />
        </button>
      </div>
    </b-row>

    <b-table class="mb-0" hover bordered :fields="fields" :items="list">
      <template slot="HEAD_id" slot-scope="data">
        <b-form-checkbox v-model="allCampaignsSelected" @click.native.stop>
          {{ data.label }}
        </b-form-checkbox>
      </template>

      <template slot="id" slot-scope="{ item }">
        <b-form-checkbox
          v-model="selectedCampaigns"
          :value="{ id: item.id, name: item.name }"
        >
          {{ item.id }}
        </b-form-checkbox>
      </template>

      <template slot="status" slot-scope="{ item }">
        <b-badge
          :variant="statusClass(item.info.status)"
          class="text-uppercase"
        >
          {{ item.info.status }}
        </b-badge>
      </template>

      <template slot="actions" slot-scope="{ item }">
        <div v-if="!archivedList" class="text-center">
          <b-dropdown
            toggle-class="p-0 border-0"
            right
            variant="link"
            size="lg"
            no-caret
          >
            <template slot="button-content">
              <svg-icon icon="dots" class="icon-lg align-top txt-3-alt" />
            </template>
            <!-- <b-dropdown-item class="small font-weight-bold">
                START
              </b-dropdown-item> -->
            <b-dropdown-item
              @click="showRawData(item)"
              class="small font-weight-bold"
            >
              VIEW RAW DATA
            </b-dropdown-item>
            <b-dropdown-item class="small font-weight-bold">
              STATISTICS
            </b-dropdown-item>
            <b-dropdown-item class="small font-weight-bold">
              EDIT
            </b-dropdown-item>
            <b-dropdown-item
              class="small font-weight-bold text-danger"
              @click="showConfirm('archive', item)"
            >
              DELETE
            </b-dropdown-item>
          </b-dropdown>
        </div>
        <button
          v-else
          class="btn-link-alt btn-link-primary"
          v-b-tooltip.hover
          title="Restore campaigns"
          @click="showConfirm('restore', item)"
        >
          <svg-icon icon="loop" class="icon-xl" />
        </button>
      </template>

      <!-- <template slot="bottom-row">
          <td :class="fields.id.class">Summary</td>
          <td :class="fields.name.class"></td>
          <td :class="fields.type.class"></td>
          <td :class="fields.clicks.class">
            {{ formatNumber(totals.clicks) }}
          </td>
          <td :class="fields.ctr.class">{{ formatCtr(totals.ctr) }}</td>
          <td :class="fields.impressions.class">
            {{ formatNumber(totals.impressions) }}
          </td>
          <td :class="fields.ecpm.class">{{ formatCpm(totals.ecpm) }}</td>
          <td :class="fields.cost.class">{{ formatCost(totals.cost) }}</td>
          <td :class="fields.status.class"></td>
          <td :class="fields.actions.class"></td>
        </template> -->
    </b-table>

    <b-modal
      id="new-group-form"
      ref="new-group-form"
      hide-footer
      title="Add new group"
      size="sm"
    >
      <b-form>
        <b-form-label
          slot="label"
          label="Group name"
          for="group-name"
          :errors="errors.name"
        />
        <b-input
          id="group-name"
          class="mb-2 mr-sm-2 mb-sm-0"
          placeholder="My awesome group"
          :state="errors.name ? false : null"
          v-model="newGroupName"
        />
        <div class="mt-2 text-right">
          <b-button
            size="sm"
            variant="primary"
            @click="submitGroup"
            :disabled="!submitState"
          >
            Save
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <b-modal
      id="raw-campaign"
      ref="raw-campaign"
      hide-footer
      title="Raw campaign info"
      size="lg"
    >
      <pre>
        {{ rawCampaign }}
      </pre>
    </b-modal>
  </div>
</template>

<script>
import { statusClassesMixin } from '@/utils/mixins.js';

export default {
  mixins: [statusClassesMixin],

  props: {
    list: {
      type: Array,
      default() {
        return [];
      }
    },
    totals: {
      type: Object,
      default() {
        return {};
      }
    }
  },

  data() {
    return {
      fields: {
        id: {
          label: 'ID',
          key: 'info.id',
          thClass: 'pl-2',
          class: ''
        },
        name: {
          label: 'Name',
          key: 'info.name',
          sortable: true,
          class: '',
          tdClass: 'pl-5'
        },
        clicks: {
          label: 'Clicks',
          key: 'stats.clicks',
          sortable: true,
          formatter: this.formatNumber,
          class: '',
          tdClass: 'pl-5'
        },
        ctr: {
          label: 'CTR',
          key: 'stats.ctr',
          sortable: true,
          formatter: this.formatCtr,
          class: '',
          tdClass: 'pl-5'
        },
        impressions: {
          label: 'Imps.',
          key: 'stats.impressions',
          sortable: true,
          formatter: this.formatNumber,
          class: '',
          tdClass: 'pl-5'
        },
        ecpm: {
          label: 'eCPM',
          key: 'stats.ecpm',
          sortable: true,
          formatter: this.formatCpm,
          class: '',
          tdClass: 'pl-5'
        },
        bid: {
          label: 'Cost',
          key: 'info.bid',
          sortable: true,
          formatter: this.formatCost,
          class: '',
          tdClass: 'pl-5'
        },
        status: {
          label: 'Status',
          key: 'info.status',
          class: '',
          tdClass: 'pl-5'
        },
        actions: {
          label: '',
          key: 'actions',
          thStyle: {
            width: '60px'
          },
          tdClass: 'text-center',
          class: ''
        }
      },
      columnsVisible: {
        id: {
          label: 'ID',
          visible: true
        },
        name: {
          label: 'Name',
          visible: true
        },
        type: {
          label: 'ID',
          visible: true
        }
      },
      selectedCampaigns: [],
      allCampaignsSelected: false,
      confirmDialogSettings: {
        title: 'Please Confirm',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'YES',
        cancelTitle: 'NO',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      },
      confirmMessages: {
        start: 'Please confirm that you want to start campaign(s): ',
        pause: 'Please confirm that you want to pause campaign(s): ',
        archive: 'Please confirm that you want to archive campaign(s): ',
        restore: 'Please confirm that you want to restore campaign(s): '
      },
      alertMessages: {
        start: 'Campaign(s) started.',
        pause: 'Campaign(s) paused.',
        archive: 'Campaign(s) archived.',
        restore: 'Campaign(s) restored.'
      },
      newGroupName: '',
      errors: {},
      rawCampaign: ''
    };
  },

  computed: {
    userId() {
      return this.$store.getters.user.id;
    },
    archivedList() {
      return this.$router.currentRoute.name === 'archived-campaigns';
    },
    campaignsIds() {
      return this.list.map(campaign => {
        return {
          id: campaign.id,
          name: campaign.name
        };
      });
    },
    tableFields() {
      return Object.keys(this.fields);
    },
    submitState() {
      return this.newGroupName.length > 0;
    }
  },

  watch: {
    allCampaignsSelected(checked) {
      this.selectedCampaigns = checked ? this.campaignsIds.slice() : [];
    }
  },

  methods: {
    showRawData(campaign) {
      this.rawCampaign = JSON.stringify(campaign, null, 2);
      this.$refs['raw-campaign'].show();
    },
    formatNumber(val) {
      return this.$numeral(val).format('0,0');
    },
    formatCtr(val) {
      return this.$numeral(val).format('0.00%');
    },
    formatCpm(val) {
      return this.$numeral(val).format('$0,0.0000');
    },
    formatCost(val) {
      return this.$numeral(val).format('$0,0.00');
    },
    convertToCSV(args) {
      var result, ctr, keys, columnDelimiter, lineDelimiter, data;

      data = args.data || null;
      if (data == null || !data.length) {
        return null;
      }

      columnDelimiter = args.columnDelimiter || ',';
      lineDelimiter = args.lineDelimiter || '\n';

      keys = Object.keys(data[0]);

      result = '';
      result += keys.join(columnDelimiter);
      result += lineDelimiter;

      data.forEach(function(item) {
        ctr = 0;
        keys.forEach(function(key) {
          if (ctr > 0) result += columnDelimiter;

          result += item[key];
          ctr++;
        });
        result += lineDelimiter;
      });

      return result;
    },
    downloadCSV(args) {
      var data, filename, link;
      var csv = this.convertToCSV({
        data: this.list
      });
      if (csv == null) return;

      filename = args.filename || 'export.csv';

      if (!csv.match(/^data:text\/csv/i)) {
        csv = 'data:text/csv;charset=utf-8,' + csv;
      }
      data = encodeURI(csv);

      link = document.createElement('a');
      link.setAttribute('href', data);
      link.setAttribute('download', filename);
      link.click();
    },
    showConfirm(action, campaign) {
      const campaigns = !campaign
        ? this.selectedCampaigns.map(campaign => campaign.name).join(', ')
        : campaign.name;
      this.$bvModal
        .msgBoxConfirm(
          this.confirmMessages[action] + campaigns,
          this.confirmDialogSettings
        )
        .then(value => {
          if (!value) return false;
          this.$notify('alert', {
            text: this.alertMessages[action],
            variant: 'success',
            time: 5000
          });
        });
    },
    submitGroup() {
      const newGroup = { name: this.newGroupName };
      this.$api.adnet.post(`/v0/advert-groups/${this.userId}`, newGroup).then(
        () => {
          this.errors = {};
          this.$refs['new-group-form'].hide();
          this.$notify('alert', {
            text: `${this.newGroupName} group created.`,
            variant: 'success',
            time: 5000
          });
          this.newGroupName = '';
        },
        e => {
          this.$refs['new-group-form'].hide();
          this.errors = {};

          if (e.response.data.errors) {
            this.errors = this.$parseErrors(e.response.data.errors) || {};
          } else {
            e.response.data.detail &&
              this.$notify('alert', {
                text: e.response.data.detail,
                variant: 'danger',
                time: 0
              });
          }
        }
      );
    }
  }
};
</script>

<style lang="scss">
.b-table-bottom-row > td {
  color: $primary;
  border: none;
  border-bottom: 1px solid $border-color;

  &:first-child {
    border-left: 1px solid $border-color;
  }

  &:last-child {
    border-right: 1px solid $border-color;
  }
}

.campaigns-controls {
  margin-top: 6px;

  button {
    display: inline-block;
    margin-right: 7px;

    &:disabled {
      cursor: default;

      svg {
        color: $light;
      }
    }

    svg {
      color: $primary;
    }

    &:last-child {
      svg {
        position: relative;
        top: -1px;
        font-size: 1.1rem;
      }
    }
  }

  &.table-controls {
    svg {
      font-size: 1.1rem;
    }
  }
}

.table-columns-toggler {
  margin-left: 15px;

  .dropdown-menu {
    padding: 10px;
  }

  i > svg {
    font-size: 1rem;
    position: relative;
    top: 1px;
  }
}
</style>
